// features/findings/korea-hungary-divergence.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — Korea vs Hungary: What Pronatalist Spending Can Buy (v1)
// Form 2 (Pair comparison). Second published Lab finding.
// Content sourced verbatim from docs/LAB_FINDING_KOREA_HUNGARY_V1.md
// ════════════════════════════════════════════════════════════════════
// Reflexivity HIGH · Population-decline domain · Drew from Continuum
// Phase A Batch 1 hypothesis-testing work for the analytical framework.
// Pre-registered 2026-05-07.
// ════════════════════════════════════════════════════════════════════

import {
  FindingBack,
  FindingHeader,
  FindingSection,
  FindingSubhead,
  FindingPill,
  FindingPillRow,
  ChartSlot,
} from './components-shared';
import {
  DivergenceTimeline,
  ParallelCoordinates,
  CausalChainDiagram,
  CountryScatter,
  TornadoPlot,
  PredictionsBlock,
} from './charts';

// ─── Chart data for this finding ─────────────────────────────────────

const TIMELINE_SERIES = [
  {
    id: 'korea',
    label: 'Korea',
    color: 'oxblood',
    points: [
      { x: 1990, y: 1.57 },
      { x: 1995, y: 1.63 },
      { x: 2000, y: 1.45 },
      { x: 2003, y: 1.16 },
      { x: 2005, y: 1.08 },
      { x: 2010, y: 1.23 },
      { x: 2015, y: 1.24 },
      { x: 2018, y: 0.98 },
      { x: 2021, y: 0.81 },
      { x: 2023, y: 0.72 },
      { x: 2024, y: 0.75 },
    ],
  },
  {
    id: 'hungary',
    label: 'Hungary',
    color: 'brass',
    points: [
      { x: 1990, y: 1.83 },
      { x: 1995, y: 1.57 },
      { x: 2000, y: 1.32 },
      { x: 2002, y: 1.27 },
      { x: 2005, y: 1.31 },
      { x: 2010, y: 1.25 },
      { x: 2015, y: 1.45 },
      { x: 2018, y: 1.55 },
      { x: 2021, y: 1.59 },
      { x: 2023, y: 1.51 },
      { x: 2024, y: 1.38 },
    ],
  },
];

const WEDGE_DIMENSIONS = [
  {
    id: 'spending',
    label: 'Spending intensity',
    min: 0,
    max: 7,
    minLabel: '0% GDP',
    maxLabel: '6.2% GDP',
  },
  {
    id: 'narrative',
    label: 'Civic narrative density',
    min: 0,
    max: 5,
    minLabel: 'low',
    maxLabel: 'high',
  },
  {
    id: 'refugia',
    label: 'Refugia density',
    min: 0,
    max: 5,
    minLabel: 'minimal',
    maxLabel: 'substantial',
  },
];

const WEDGE_ENTITIES = [
  {
    id: 'hungary',
    label: 'Hungary',
    color: 'brass',
    values: { spending: 6.2, narrative: 4, refugia: 3 },
  },
  {
    id: 'korea',
    label: 'Korea',
    color: 'oxblood',
    values: { spending: 1.65, narrative: 1, refugia: 1 },
  },
];

const KH_CHAIN_STEPS = [
  { label: 'Policy → marginal birth-cost reduction', confidence: 0.85 },
  { label: 'Marginal couples shift birth timing earlier (tempo)', confidence: 0.80 },
  { label: 'Tempo recovery saturates — postponement-catch-up plays through', confidence: 0.75 },
  { label: 'Cohort fertility responds to deeper variables (narrative, infrastructure, status)', confidence: 0.65 },
  { label: 'Post-tempo equilibrium reverts toward pre-policy cohort rate', confidence: 0.70 },
];

const SCATTER_POINTS = [
  // Spending share of GDP × TFR change 2010-2024
  { label: 'Korea',     x: 1.65, y: -0.48, tone: 'oxblood' },
  { label: 'Hungary',   x: 4.20, y: 0.13,  tone: 'brass' },
  { label: 'Italy',     x: 2.10, y: -0.16, tone: 'neutral' },
  { label: 'Spain',     x: 1.30, y: -0.05, tone: 'neutral' },
  { label: 'Japan',     x: 1.95, y: -0.14, tone: 'neutral' },
  { label: 'France',    x: 2.91, y: -0.16, tone: 'neutral' },
  { label: 'Estonia',   x: 3.29, y: -0.10, tone: 'plum' },
  { label: 'Israel',    x: 1.10, y: 0.05,  tone: 'sage', outlier: true, labelDx: -50, labelDy: -8 },
];

const KH_FALSIFIER_DIMS = [
  {
    id: 'tempo-share',
    label: 'Tempo-vs-cohort share of HU rise',
    low: -0.20,
    high: 0.10,
    lowLabel: 'all tempo — finding holds',
    highLabel: 'real cohort gain — finding weakens',
  },
  {
    id: 'reversion-sustains',
    label: 'HU 2022-24 reversion sustains',
    low: -0.15,
    high: 0.20,
    lowLabel: 'continues falling — finding holds',
    highLabel: 'reverses — premature claim',
  },
  {
    id: 'kr-uptick',
    label: 'KR 0.75 uptick character',
    low: -0.10,
    high: 0.15,
    lowLabel: 'tempo only — pattern holds',
    highLabel: 'cohort change — pattern incomplete',
  },
  {
    id: 'cross-domain',
    label: 'Cross-domain replication',
    low: -0.20,
    high: 0.20,
    lowLabel: 'sports/Western only',
    highLabel: 'transfers to East Asian, MENA, Latin contexts',
  },
];

export const KH_PREDICTIONS = [
  {
    label: 'Hungary TFR 2027',
    band: [1.30, 1.50],
    central: 1.40,
    falsifierBand: [1.20, 1.65],
    resolutionDate: '2028 KSH release',
    status: 'open',
    note: 'Tests tempo-without-cohort interpretation. Bands derived from underlying cohort trajectory once tempo is removed.',
  },
  {
    label: 'Korea TFR 2027',
    band: [0.70, 0.90],
    central: 0.78,
    falsifierBand: [0.65, 1.00],
    resolutionDate: '2028 Statistics Korea release',
    status: 'open',
    note: 'Wide band reflects lowest-low position making small movements possible in either direction.',
  },
  {
    label: 'Anti-prediction: no sub-replacement country produces sustained cohort recovery to TFR 1.7+ via spending alone over 2025-2035.',
    resolutionDate: '2030+ cohort completion data',
    status: 'open',
    note: 'Tests the broader claim across the OECD declining-fertility cluster. Falsifying example: any nation crosses 1.7 cohort fertility for the 1990-1995 birth cohort with the spending-without-narrative-without-refugia structural profile.',
  },
];

export const FINDING_META = {
  slug: 'korea-hungary-divergence',
  domain: 'popdec',
  year: 2026,
  version: 'v1',
  lifecycle: 'pre-registered',
  prevVersion: null,
  revisedBy: null,
  whatChanged: null,
  retractionNote: null,
  citationHandle: 'lab:finding/popdec/2026/korea-hungary-divergence/v1',
  title: 'Korea vs Hungary: What Pronatalist Spending Can Buy — and What It Can\'t',
  shortTitle: 'Korea vs Hungary',
  pageTitle: 'Korea vs Hungary — Lab Finding · Shift',
  description: 'Hungary spent 6.2% of GDP on family policy and lifted TFR from 1.25 to 1.59. Then it reversed. The pair shows what policy can move and what it can\'t reach.',
  form: 2,
  formName: 'Pair comparison',
  reflexivity: 'HIGH',
  epistemicStatus: 'mechanism',
  confidence: 'medium-low',
  sampleSize: 2,
  preregisteredAt: '2026-05-07',
  visibility: 'public-peek',
};

export default function KoreaHungaryDivergenceFinding() {
  return (
    <article className="finding-article">
      <FindingBack href="/findings" label="← Findings index" />

      <FindingHeader
        eyebrow="Population Decline · Korea-Hungary divergence · 2026-05-07"
        title="Korea vs Hungary"
        sub="What pronatalist spending can buy — and what it can't."
        subItalic="Hungary spent 6.2% of GDP on family policy and lifted TFR from 1.25 to 1.59 over a decade. Then it reversed. Korea, with much smaller spending, kept declining to 0.72 then ticked up to 0.75 in 2024. The pair shows what policy can move and what it can't reach."
        meta={FINDING_META}
      />

      {/* ─── Lede ─── */}
      <section className="finding-lede">
        <p>
          Korea and Hungary entered the 21st century in similar demographic territory: post-communist or post-authoritarian transition (Hungary explicitly; Korea via democratization), urbanizing, with TFR falling toward sub-replacement. By 2010, both had named demographic decline as a national priority. Then the responses diverged. Hungary under Viktor Orbán committed to one of Europe&rsquo;s largest pronatalist spending programs — peaking at 6.2% of GDP — under an explicit &ldquo;demographic security&rdquo; framing. Korea launched its first Low Fertility Aging Society Master Plan in 2006 with much less intensity. Fifteen years on, the comparison reveals something the Hungary-as-success-case framing got wrong: <strong>policy intensity moves the timing of births, not the underlying willingness to have them.</strong> Hungary&rsquo;s measured TFR rose, peaked at 1.59 in 2021, and then reversed — back to 1.38 by 2024. Korea fell to 0.72, then ticked up to 0.75 in 2024 as pandemic-delayed marriages cleared. Neither country has produced sustained cohort fertility recovery. The wedge between them isn&rsquo;t what made one win; it&rsquo;s what made one <em>appear</em> to win for a decade before the same underlying structure reasserted itself.
        </p>
      </section>

      {/* ─── TL;DR pill card ─── */}
      <section className="finding-tldr">
        <div className="finding-tldr-eyebrow">TL;DR</div>
        <p className="finding-tldr-verdict">
          Pronatalist policy intensity (spending share of GDP) produces measurable short-term TFR movement, predominantly via tempo effects. <strong>Sustained cohort fertility recovery requires a different mechanism.</strong> Hungary&rsquo;s 1.25→1.59→1.38 arc and Korea&rsquo;s 0.92→0.72→0.75 arc both fit the tempo-without-cohort interpretation.
        </p>
        <FindingPillRow>
          <FindingPill tone="mechanism">Mechanism documented</FindingPill>
          <FindingPill tone="warn">Confidence: medium-low</FindingPill>
          <FindingPill tone="neutral">N=2 + 4 anchor cases</FindingPill>
          <FindingPill tone="neutral">Pre-registered 2026-05-07</FindingPill>
          <FindingPill tone="failure">Reflexivity: HIGH</FindingPill>
        </FindingPillRow>
        <div className="finding-tldr-corpus">
          <strong>Corpus:</strong> Korea + Hungary (primary pair). Israel, Estonia, France, Iran (anchor cases for sustained-recovery vs counter-case mechanism). Drew from Continuum Phase A Batch 1 hypothesis-testing work.
        </div>
      </section>

      {/* ─── § The two cases ─── */}
      <FindingSection eyebrow="§ The two cases" title="Korea and Hungary in 2000-2005 looked structurally comparable.">
        <p className="finding-prose">
          Both had completed political transitions in the prior decade. Both were urbanizing rapidly. Both had education systems that drove female labor-force participation up quickly. Both had fertility falling fast: Hungary&rsquo;s TFR fell from 1.83 in 1990 to 1.27 in 2002; Korea&rsquo;s fell from 1.57 in 1990 to 1.16 in 2003. By the early 2000s, both governments had named demographic decline as a strategic concern.
        </p>

        <p className="finding-prose">
          The economies differed in scale (Korea&rsquo;s GDP per capita roughly 2x Hungary&rsquo;s) but the <em>shape</em> of the demographic problem was similar: rapid economic modernization had restructured family formation, marriage was being delayed and forgone, women&rsquo;s educational and labor-force gains were not being matched by changes in domestic-labor or career-flexibility norms, and housing dynamics in major metros were shifting. Both countries had also seen weakening church/civic infrastructure (Hungary: post-communist secularization on top of an already-secular state legacy; Korea: rapid urbanization detaching populations from village-temple-Confucian structures).
        </p>

        <p className="finding-prose">
          <strong>Where they diverged was in the response.</strong> Hungary&rsquo;s response — particularly after Orbán&rsquo;s 2010 return to power — was a comprehensive, ideologically-loaded pronatalist program. Korea&rsquo;s response was a sequence of technocratic Low Fertility Aging Society Master Plans (the first in 2006), heavier on workplace and childcare policy than on direct family transfers, and considerably smaller in fiscal scale.
        </p>

        <DivergenceTimeline
          title="Korea + Hungary TFR 1990-2024 — annotated divergence"
          series={TIMELINE_SERIES}
          splitPoint={{ x: 2010, label: '2010 — Orbán returns; HU policy diverges' }}
          yLabel="TFR (births per woman)"
          xLabel="year"
          yMin={0.5}
          yMax={2.0}
        />
      </FindingSection>

      {/* ─── § The split ─── */}
      <FindingSection eyebrow="§ The split" title="The deviation point is 2010.">
        <p className="finding-prose">
          Earlier candidates exist — Hungary&rsquo;s 1990 transition shock; Korea&rsquo;s 1997 IMF crisis; both nations&rsquo; family policy launches in 2005-2006 — but 2010 is when the <em>responses</em> diverge into recognizably different programs.
        </p>

        <p className="finding-prose">
          In Hungary, Orbán&rsquo;s 2010 return to power introduced demographic-security framing as a core political identity. The 2011 constitution explicitly references the family. Major policy moves followed: in 2015, the CSOK (Family Housing Allowance) program for families with children buying homes; in 2019, the &ldquo;Family Protection Action Plan&rdquo; including a $35,000 (10M HUF) loan forgiven progressively per child, lifetime income tax exemption for women with 4+ children, and substantial subsidies for larger families. Direct family-policy spending climbed: roughly 3.5% of GDP by 2018, peaking near 6.2% of GDP by some accounts in 2022 (when transfer programs combined with COVID family-support reached their largest scale).
        </p>

        <p className="finding-prose">
          In Korea, the 2nd Low Fertility Aging Society Master Plan (2010-2015) increased funding but kept the technocratic frame: workplace work-family-balance reforms, expanded childcare subsidies, parental leave reforms, housing assistance for newlyweds. Direct family-policy spending climbed but stayed roughly 1.0-1.7% of GDP across the 2010-2024 window. The political framing remained crisis-management rather than identity-defining; no major Korean political coalition built its identity around demographic recovery.
        </p>

        <p className="finding-prose">
          <strong>Three properties of the response diverged sharply at and after 2010:</strong>
        </p>
        <ol className="finding-numbered-list">
          <li><strong>Spending intensity.</strong> Hungary: roughly 4-6.2% GDP at peak. Korea: roughly 1-1.7%. A 3-5x difference in fiscal commitment.</li>
          <li><strong>Civic narrative density around fertility.</strong> Hungary: explicit Christian-conservative-nationalist framing where fertility is national-survival material. Korea: technocratic problem-solving framing, fertility as a workplace + housing + childcare optimization problem. The narrative carries cultural weight Korea&rsquo;s framing did not.</li>
          <li><strong>Refugia preservation.</strong> Hungary: Roma sub-populations with TFR ~3+ remain present and are demographically non-trivial; rural religious-traditional pockets persist. Korea: refugia (rural Christian communities, traditional Confucian-rural pockets) are demographically minor and shrinking.</li>
        </ol>

        <p className="finding-prose">
          These three wedge variables track the H-13/H-16 framework from the team&rsquo;s Phase A Batch 1 hypothesis testing — refugia density + cultural-narrative engagement as the case-specific protective mechanism in declining fertility populations. The wedge isn&rsquo;t new to this finding; what this finding adds is the documented test of the wedge&rsquo;s strength via the comparison.
        </p>
      </FindingSection>

      {/* ─── § The wedge ─── */}
      <FindingSection eyebrow="§ The wedge" title="Hungary moved TFR; Korea didn't (much). Then Hungary's gain reversed.">
        <p className="finding-prose">
          The data, accepting current-state numbers (2024):
        </p>
        <ul className="finding-bullets">
          <li><strong>Hungary:</strong> 1.25 (2010 trough) → 1.59 (2021 peak) → 1.38 (2024). Net 2010-2024: +0.13.</li>
          <li><strong>Korea:</strong> 1.23 (2010) → 0.72 (2023) → 0.75 (2024). Net 2010-2024: -0.48.</li>
        </ul>

        <p className="finding-prose">
          The first read is <em>Hungary&rsquo;s policies worked, Korea&rsquo;s didn&rsquo;t.</em> The data accommodates this read, but only superficially. Three corrections sharpen the actual wedge.
        </p>

        <FindingSubhead>Correction 1 — Most of Hungary&rsquo;s TFR rise was tempo, not cohort.</FindingSubhead>
        <p className="finding-prose">
          The independent demographic analyses converge on a single quantitative claim: <strong>Hungary&rsquo;s 2010-2021 raw TFR rose 27% (1.25 → 1.59), while tempo-adjusted period fertility (TFRp* via the Bongaarts-Feeney method) remained essentially flat at 1.5-1.6 across the same window</strong> (<a href="https://www.niussp.org/fertility-and-reproduction/evaluating-pronatalist-policies-with-tfr-brings-misleading-conclusions-examples-fromhungary/" target="_blank" rel="noopener">Lutz, Sobotka, Zeman, N-IUSSP, March 2024</a>). The mean age at first birth among Hungarian women rose from 25.1 (2000) to 27.7 (2010), reflecting earlier postponement; the 0.7-year incremental rise to 28.4 (2020) was the tail end of that postponement decelerating — not a change in completed family size. Cohort fertility at age 40 for Hungarian women born in the 1960s and 1970s shows a steadily declining series with no sign of turnaround. The headline TFR rise was a tempo signal; the cohort fertility series was not.
        </p>
        <p className="finding-prose">
          The Frontiers in Sociology analysis (<a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC9755853/" target="_blank" rel="noopener">Berde & Drabancz, 2022</a>) reaches the same conclusion via independent calculation: TFRp* barely exceeded raw TFR in 2011-2020, whereas the gap had previously been &gt;0.4 — a narrowing gap is the signature of fertility recuperation (delayed births being made up), not a real change in family size intent. The American Enterprise Institute&rsquo;s November 2025 review (<a href="https://www.aei.org/op-eds/hungarys-fertility-outcomes-highlight-pro-natal-policy-limitations/" target="_blank" rel="noopener">Brown Calder</a>) documents the post-peak reversion: 2020 peak 1.61 → 2023 1.51 → 2024 1.39. <strong>The 1.59 peak was a transient; the reversion to 1.39 is the underlying cohort signal returning.</strong>
        </p>
        <p className="finding-prose">
          <strong>One partial dissent in the literature.</strong> An earlier <a href="https://ifstudies.org/blog/is-hungary-experiencing-a-policy-induced-baby-boom" target="_blank" rel="noopener">Institute for Family Studies analysis (Lyman Stone, July 2018)</a> argued the policy effect operates through marriage incentives rather than direct childbearing — noting that third-or-higher parity births had fallen while first and second rose. Stone&rsquo;s analysis predates the 2021 peak and the 2022-2024 reversion, so it cannot speak to the tempo-vs-cohort question across the full window. The marriage-mediation hypothesis is consistent with the tempo interpretation: pulling marriages forward also pulls first-and-second births forward (the tempo recovery) without changing completed fertility. Stone&rsquo;s position is not load-bearing dissent; it is mechanism within tempo.
        </p>
        <p className="finding-prose">
          <strong>The cohort-completion-data caveat remains.</strong> Hungarian women born 1985-1995 will not complete cohort fertility until ~2030; the strongest version of the tempo-not-cohort claim awaits that data. Until then, the attribution rests on (a) tempo-adjusted period TFR via Bongaarts-Feeney TFRp* showing a flat series 2010-2020, (b) cohort-completion-by-age tables for Hungarian women aged 40 born 1960s-70s showing continued decline, (c) the 2022-2024 reversion to 1.39 consistent with the underlying cohort trajectory returning. If the 1985-1995 Hungarian cohort completes meaningfully above the projected 1.50 — say, ≥1.65 — the tempo-without-cohort interpretation is wounded. <code>lab:finding/popdec/2026/positive-case-search/v1</code> P3 is the explicit pre-registered prediction that resolves this.
        </p>

        <FindingSubhead>Correction 2 — Korea&rsquo;s 2024 0.75 is also probably tempo.</FindingSubhead>
        <p className="finding-prose">
          Korea&rsquo;s 2024 increase from 0.72 to 0.75 is the first uptick in nine years. Statistics Korea attributed the rise primarily to a marriage rebound — couples who delayed marriage during the COVID years finally tying the knot, with births following 12-18 months later. That&rsquo;s a tempo bump, not a cohort change. The underlying baseline TFR may be even lower than 0.72; the 0.72-0.75 oscillation is partly artifact of pandemic disruption clearing.
        </p>

        <FindingSubhead>Correction 3 — what spending didn&rsquo;t buy.</FindingSubhead>
        <p className="finding-prose">
          If Hungary&rsquo;s 1.59 was tempo and the 1.38 reversion is the underlying cohort signal returning, then 4-6.2% of GDP for over a decade bought a temporary timing shift, not sustained cohort recovery. The honest summary: Hungary&rsquo;s pronatalist program produced short-run TFR movement but didn&rsquo;t change the underlying willingness or capacity to have larger families across women&rsquo;s lives. Korea, spending one-third to one-fifth as much, didn&rsquo;t produce a comparable timing shift — but its underlying cohort trajectory may not be dramatically different from Hungary&rsquo;s.
        </p>

        <ParallelCoordinates
          title="Wedge variables — three dimensions where Hungary and Korea diverged at 2010"
          dimensions={WEDGE_DIMENSIONS}
          entities={WEDGE_ENTITIES}
        />

        <p className="finding-prose">
          The wedge that <em>did</em> matter, when separated from the tempo confound:
        </p>
        <ul className="finding-bullets">
          <li><strong>Spending intensity</strong> moved tempo by 5-10 years for some women in Hungary. Did not produce sustained cohort change.</li>
          <li><strong>Civic-narrative density</strong> had a measurable effect on the public legitimacy of the policy in Hungary (which sustained political support for spending) but did not translate into a cohort fertility signal.</li>
          <li><strong>Refugia density</strong> is the variable that, in the wider corpus, distinguishes places where sub-population fertility resists the broader decline. Hungary&rsquo;s Roma sub-population sustains TFR 3+ but at population scale this is a partial offset, not a national-recovery mechanism.</li>
        </ul>

        <p className="finding-prose">
          <strong>The pair tells us what spending can buy: tempo. The pair tells us what the wedge variables don&rsquo;t deliver: sustained cohort recovery.</strong> That&rsquo;s a real finding, but it&rsquo;s not the finding most Hungary-as-success-case writeups suggest.
        </p>
      </FindingSection>

      {/* ─── § The mechanism ─── */}
      <FindingSection eyebrow="§ The mechanism" title="Why does pronatalist spending produce tempo without cohort?">
        <p className="finding-prose">
          The causal chain, traced step by step:
        </p>

        <ol className="finding-numbered-list">
          <li><strong>Policy → marginal birth-cost reduction.</strong> Direct family transfers, housing subsidies, tax breaks reduce the incremental cost of having a child for some marginal couples — those who were close to deciding to have a (next) child. The reduction is real and shows up in the data.</li>
          <li><strong>Marginal couples shift birth timing earlier.</strong> A couple that would have had a child at 35 has it at 33 instead. A couple that would have had a 2nd child at 38 has it at 36. The data shows up as <em>more births now</em> — which inflates period TFR — but the total number of children per woman over her life is unchanged.</li>
          <li><strong>Tempo recovery saturates.</strong> Once the postponement-catch-up has played through (most in-window women have made their fertility decisions), there&rsquo;s no more tempo to recover. Period TFR falls back toward the cohort rate.</li>
          <li><strong>Cohort fertility responds to deeper variables.</strong> Whether a woman has 2 children vs 1 vs 3 over her life depends on factors policy can affect at the margin (cost) but not directly: meaning structures around motherhood, time-flexibility (not just money), peer-network reproductive density, status hierarchies around children, future-orientation as a measurable construct, and infrastructure for cooperative reproduction (not just paid childcare slots).</li>
          <li><strong>In the absence of cohort-affecting changes, the post-tempo equilibrium is at or below the pre-policy cohort rate.</strong> Hungary&rsquo;s 1.38 in 2024 is not far from where the underlying 2010 trajectory would have predicted if policy had moved nothing.</li>
        </ol>

        <CausalChainDiagram
          title="Causal chain — policy → tempo → saturation → cohort-ceiling → reversion"
          steps={KH_CHAIN_STEPS}
        />

        <p className="finding-prose">
          <strong>What&rsquo;s the mechanism that DOES produce cohort recovery?</strong> The pair doesn&rsquo;t show it, because neither case demonstrates it. The wider corpus — Israel TFR 3.0+, Mormon Utah, Hungary Roma, French-Canadian Quebec pre-1965 — points at a multi-component answer the Continuum team&rsquo;s Phase A work has been narrowing:
        </p>
        <ul className="finding-bullets">
          <li><em>Existential-narrative density</em> (Israel&rsquo;s specific architecture: military-survival framework, ongoing civilizational continuity narrative)</li>
          <li><em>Cooperative reproduction infrastructure</em> (kin-substitute networks, religious-community child-rearing capacity, refugium-density)</li>
          <li><em>Status hierarchies that elevate child-rearing as high-status activity</em> (rather than a career-disruptor cost)</li>
          <li><em>Future-orientation embedded in cultural transmission</em> (intergenerational reciprocity expectations remain operative)</li>
        </ul>

        <p className="finding-prose">
          <strong>These are not policy levers in any direct sense.</strong> They&rsquo;re cultural-structural properties that take generations to build and can be lost in a generation. Pronatalist spending can compensate at the margins; it cannot create them. <em>That&rsquo;s the deepest finding the Korea-Hungary pair documents.</em>
        </p>
      </FindingSection>

      {/* ─── § What generalizes ─── */}
      <FindingSection eyebrow="§ What generalizes" title="The through-line beyond Korea and Hungary.">
        <p className="finding-prose">
          Across declining-fertility advanced economies (Korea, Italy, Spain, Japan, Hungary, the broader EU low-low cluster), the policy-intensity-vs-TFR-recovery relationship is weak and largely tempo-driven. The countries with sustained recovery are the cases that have <em>non-policy</em> protective mechanisms — Israel&rsquo;s existential narrative + religious sub-population reproductive density; Mormon Utah&rsquo;s distinct cultural-religious lineage; the patchwork of religious-traditional sub-populations in some declining nations that maintain elevated TFR locally without national-level effect.
        </p>

        <p className="finding-prose">
          <strong>The honest implication:</strong> <em>the pronatalist policy debate is largely about the wrong lever.</em> Spending changes timing, occasionally significantly. It doesn&rsquo;t produce the cohort fertility recovery countries are reaching for. The structural levers — narrative, infrastructure, status, transmission — operate at cultural-evolutionary timescales and are not directly policy-tractable in the conventional sense.
        </p>

        <p className="finding-prose">
          <strong>This generalizes to a stronger claim:</strong> any country at sub-replacement TFR that hopes to recover via spending alone is misidentifying the mechanism. The Korea-Hungary pair is the cleanest documented test of this proposition, but the broader corpus supports it. Israel&rsquo;s TFR is exceptional precisely because Israel has the structural mechanism others lack; emulating Israel by raising spending levels won&rsquo;t replicate Israel&rsquo;s outcome.
        </p>

        <CountryScatter
          title="Family-policy spending vs TFR change 2010-2024"
          points={SCATTER_POINTS}
          xLabel="family policy spending (% GDP)"
          yLabel="TFR change 2010-2024"
          xMin={0}
          xMax={6.5}
          yMin={-0.6}
          yMax={0.3}
        />
      </FindingSection>

      {/* ─── § How we could be wrong ─── */}
      <FindingSection eyebrow="§ How we could be wrong" title="The discipline requires honest exposure of where this could break.">
        <FindingSubhead>Sample size and confound exposure</FindingSubhead>
        <p className="finding-prose">
          The pair is N=2 with anchor support from ~5 additional cases. Hungary and Korea differ on so many dimensions that isolating <em>spending intensity + narrative density + refugia density</em> as the wedge variables is partly a curation choice. Other variables (initial economic shock magnitude, urbanization speed, household structure dynamics, immigration trajectory) also differ and could carry explanatory weight we&rsquo;ve assigned to the named wedge. The corpus&rsquo;s broader cases (Israel, Mormon Utah, Hungary Roma) are sub-population comparisons rather than national-policy comparisons. Treating them as evidence for the national-level mechanism requires extrapolation.
        </p>

        <FindingSubhead>Tempo vs cohort as a load-bearing distinction</FindingSubhead>
        <p className="finding-prose">
          The finding leans heavily on the claim that Hungary&rsquo;s 2010-2021 rise was tempo not cohort. If the cohort-fertility revision turns out smaller than the literature suggests — if Hungarian women born in 1985-1990 ultimately complete more children per woman than women born in 1975-1980 — then some of Hungary&rsquo;s policy-driven gain <em>is</em> real cohort change, and the finding&rsquo;s &ldquo;tempo without cohort&rdquo; framing weakens. Cohort completion data won&rsquo;t be definitive until those cohorts age out of fertility (~2030 onward).
        </p>

        <FindingSubhead>The 2022-2024 reversion may not sustain</FindingSubhead>
        <p className="finding-prose">
          Hungary&rsquo;s TFR could stabilize at 1.4-1.5 or rise again. If it does, the &ldquo;the gain reversed&rdquo; framing becomes premature. <em>Pre-registered prediction: Hungary TFR 2027 will be in [1.30, 1.50]; the prediction is falsified outside [1.20, 1.65]. Resolution: 2028 data release.</em>
        </p>

        <FindingSubhead>Korea&rsquo;s 0.75 may sustain</FindingSubhead>
        <p className="finding-prose">
          Korea&rsquo;s small uptick is currently read as tempo, but if Korea sustains 0.75-0.85 over 2025-2027, that&rsquo;s structurally meaningful — a country can exit lowest-low fertility from below if cohort dynamics shift. <em>Pre-registered prediction: Korea TFR 2027 will be in [0.70, 0.90]; predicted central estimate 0.78. Falsified outside [0.65, 1.00].</em>
        </p>

        <FindingSubhead>The mechanism&rsquo;s depth claim is the weakest link</FindingSubhead>
        <p className="finding-prose">
          <em>&ldquo;Cohort fertility responds to narrative + infrastructure + status + transmission, not policy&rdquo;</em> is a strong claim loaded into a 5-step causal chain. We&rsquo;ve supported it with the corpus&rsquo;s pattern but the mechanism specifics are inferred more than measured. The Continuum team&rsquo;s Phase 7 GDELT-based cultural-narrative-density work, when complete, will either strengthen or weaken this link.
        </p>

        <TornadoPlot
          title="Falsifier sensitivity — what shifts the finding"
          baselineLabel="finding holds"
          dimensions={KH_FALSIFIER_DIMS}
        />

        <FindingSubhead>What would change my mind</FindingSubhead>
        <p className="finding-prose">
          Three observations would force revision or retraction:
        </p>
        <ol className="finding-numbered-list">
          <li>Cohort fertility data (when available, ~2028+) showing Hungarian women born 1985-1995 completing meaningfully more children than the prior cohort, beyond the tempo-adjustment.</li>
          <li>A country at sub-replacement TFR that produces sustained cohort recovery via policy spending alone, without the narrative/infrastructure/status structural mechanisms.</li>
          <li>Cross-domain replication failure: the mechanism (tempo without cohort) doesn&rsquo;t transfer to non-Western or non-secular contexts even when policy intensity varies.</li>
        </ol>
      </FindingSection>

      {/* ─── § What this implies ─── */}
      <FindingSection eyebrow="§ What this implies" title="Reflexivity HIGH — diagnostic + menu mode, not prescriptive.">
        <p className="finding-prose">
          <em>Note on reflexivity: this finding is rated HIGH reflexivity per the Continuum reflexivity infrastructure. Predictions about fertility trajectories can affect the systems being predicted (via panic, capital flight, fertility decisions). The implications below are framed as diagnostic + menu of tested-intervention positions, not as prescriptive policy advice.</em>
        </p>

        <FindingSubhead>For demographic policymakers</FindingSubhead>
        <ul className="finding-bullets">
          <li>Pronatalist spending will produce measurable short-term TFR movement. <em>Don&rsquo;t read this as cohort recovery.</em> The political incentive to declare success on first-round TFR data is real and produces predictable mid-term embarrassment.</li>
          <li>The structural levers (narrative density, cooperative reproduction infrastructure, status-of-children, intergenerational reciprocity) are slow-build, generational. They cannot be created by policy in a 5-10 year window.</li>
          <li>Spending plus narrative is more durable than spending alone. Hungary&rsquo;s policy durability — sustaining 4%+ GDP commitment for over a decade — is itself enabled by the narrative wrapper. Without the wrapper, the spending wouldn&rsquo;t have lasted.</li>
          <li><em>Refugia preservation</em> is a policy-tractable variable that the population-decline literature underweights. Protecting and stabilizing high-fertility sub-populations preserves a structural offset that doesn&rsquo;t show up in headline national TFR but does show up in long-run cohort completion.</li>
        </ul>

        <FindingSubhead>For citizens reading this in countries facing demographic decline</FindingSubhead>
        <p className="finding-prose">
          The honest read is: a country in lowest-low fertility (TFR &lt; 1.5) faces structural constraints that policy can soften but not solve. The serious question isn&rsquo;t <em>&ldquo;what combination of subsidies will get us back to replacement?&rdquo;</em> (the answer for most declining countries is <em>&ldquo;none we can afford&rdquo;</em>) but <em>&ldquo;how do we adapt our institutions to a smaller, older population while preserving as much economic and cultural continuity as possible?&rdquo;</em> That&rsquo;s a different policy problem with different answers.
        </p>

        <FindingSubhead>Pre-registered forward predictions</FindingSubhead>
        <ol className="finding-numbered-list">
          <li>
            <strong>Hungary TFR 2027:</strong> [1.30, 1.50], central estimate 1.40. Falsified outside [1.20, 1.65]. <em>Resolution: 2028 official Hungary KSH release.</em> This prediction tests the tempo-without-cohort interpretation directly.
          </li>
          <li>
            <strong>Korea TFR 2027:</strong> [0.70, 0.90], central estimate 0.78. Falsified outside [0.65, 1.00]. <em>Resolution: 2028 official Statistics Korea release.</em>
          </li>
          <li>
            <strong>Anti-prediction:</strong> No country currently below TFR 1.5 will produce a sustained cohort fertility recovery to TFR 1.7+ via policy alone over a 2025-2035 window. <em>Resolution: 2030+ cohort completion data across the OECD declining-fertility cluster.</em>
          </li>
        </ol>

        <PredictionsBlock
          title="Pre-registered forward predictions (2026-05-07)"
          predictions={KH_PREDICTIONS}
        />
      </FindingSection>

      {/* ─── § What we're watching next ─── */}
      <FindingSection eyebrow="§ What we're watching next" title="Three open questions worth chasing.">
        <p className="finding-prose">
          <strong>1. The Hungary cohort completion question.</strong> When 2028+ data lands and we can compute Hungarian completed cohort fertility for women born in the 1985-1995 window, the tempo-vs-cohort question becomes definitively answerable. A follow-up finding (Form 3 — Falsification test) would resolve the prediction registered above.
        </p>
        <p className="finding-prose">
          <strong>2. The cross-domain mechanism transfer.</strong> The mechanism (cohort fertility responds to narrative + infrastructure + status, not spending) was extracted from a small corpus heavy on Western and middle-income cases. Does it hold in East Asian high-income contexts (Japan, Singapore, Taiwan), in Latin American contexts (Argentina, Chile, Brazil now declining), in MENA contexts (Iran post-1989 case)? The Continuum team&rsquo;s Phase 7 work is positioned to test this.
        </p>
        <p className="finding-prose">
          <strong>3. Korea&rsquo;s ultra-low recovery question.</strong> Korea is the global limit case at TFR 0.72-0.75. If Korea sustains some recovery into 0.85+, that&rsquo;s structurally meaningful. If Korea drifts back below 0.72, that&rsquo;s also a finding — a documented case of <em>no recovery is possible at this position</em>. Either resolution is published as a follow-up.
        </p>
      </FindingSection>

      {/* ─── Footer ledger ─── */}
      <footer className="finding-footer">
        <div className="finding-footer-eyebrow">Footer ledger</div>

        <dl className="finding-meta">
          <dt>Methodology Version</dt>
          <dd>Lab V2.0 (locked 2026-05-07)</dd>

          <dt>Form</dt>
          <dd>Form 2 — Pair comparison</dd>

          <dt>Reflexivity</dt>
          <dd>HIGH · <code>mode: diagnostic + menu</code> · <em>Demographic forecasts can influence the system being predicted. Output framed as diagnostic + menu of tested intervention positions; does NOT prescribe specific action.</em></dd>

          <dt>Sample size</dt>
          <dd>N=2 primary pair (Korea + Hungary); N=4-6 anchor cases (Israel, Estonia, France, Iran, Mormon Utah, Hungary Roma)</dd>

          <dt>Epistemic status</dt>
          <dd>Mechanism documented · upgrade path to causal-chain traceable contingent on Phase 7 GDELT cultural-narrative-density data + post-2028 cohort fertility data</dd>

          <dt>Pre-registered</dt>
          <dd>2026-05-07. Predictions resolve 2028 (Hungary + Korea TFR data) and across 2030+ (anti-prediction).</dd>

          <dt>Sources</dt>
          <dd>
            UN World Population Prospects 2024 · Statistics Korea (KOSIS) 2024 TFR release · Hungary Central Statistical Office (KSH) 2024 release · N-IUSSP analysis on Hungarian pronatalist policy + tempo effects · AEI op-ed on Hungary fertility outcomes · OSW Centre for Eastern Studies (2025-04-14 commentary) · Continuum team&rsquo;s Phase A Batch 1 hypothesis testing (<code>docs/CONTINUUM_PHASE_A_BATCH_1.md</code>)
          </dd>

          <dt>Falsifier</dt>
          <dd>
            Cohort fertility data (when available, ~2028+) showing Hungarian women born 1985-1995 completing meaningfully more children than prior cohorts beyond tempo adjustment; OR any country at sub-replacement TFR producing sustained cohort recovery to TFR 1.7+ via policy spending alone over 2025-2035.
          </dd>

          <dt>Citation handle</dt>
          <dd><code>lab:finding/popdec/2026/korea-hungary-divergence/v1</code></dd>
        </dl>

        <div className="finding-citation">
          <strong>Cite as:</strong> Mai, T. (2026). <em>Korea vs Hungary: What Pronatalist Spending Can Buy — and What It Can&rsquo;t.</em> Lab Finding <code>lab:finding/popdec/2026/korea-hungary-divergence/v1</code>. Methodology v2.0. Pre-registered 2026-05-07; predictions resolve from 2028.
        </div>

        <div className="finding-engage">
          <div className="finding-footer-eyebrow">How to engage</div>
          <ul className="finding-bullets">
            <li><strong>Challenge:</strong> propose a counter-case — a sub-replacement-TFR country that produced sustained cohort recovery via policy spending alone, OR a Hungarian cohort completion finding that decisively rejects the tempo-without-cohort interpretation.</li>
            <li><strong>Cite:</strong> the citation handle is stable across versions; v1 is this draft.</li>
            <li><strong>Fork:</strong> take the methodology + paired-case schema and run your own demographic-policy-evaluation comparison (Italy vs Spain; Japan vs Taiwan; Argentina vs Chile).</li>
          </ul>
        </div>

        <div className="finding-version">
          <div className="finding-footer-eyebrow">Update history</div>
          <p>v1 — 2026-05-07 — initial authoring (Tim Mai + Claude). Form 2 template applied. Drew from Continuum Phase A Batch 1 hypothesis-testing work for analytical framework. Pre-registered. Currently in Draft state.</p>
        </div>
      </footer>
    </article>
  );
}
